import { getRequestAuthPayload } from '@helpers/getRequestAuth';
import { ApiPatchOperation } from '@root/@types/types';
import type { EvalRunStatus } from '@root/domain/schemas/evals/eval-run.schema';
import type {
  CancelEvalRunPayload,
  CommentEvalFindingPayload,
  GradeEvalFindingPayload,
  ResolveEvalFindingPayload,
  StartEvalRunPayload,
} from '@root/domain/schemas/payload.schema';
import axios from 'axios';

/**
 * PBQA eval-run mutations — Contract Review Bot evaluation period. See
 * src/queries/evalRuns.ts for the read side. Kept isolated so the whole
 * feature can be removed after the evaluation wraps up.
 */

export const startEvalRun = async (
  submissionId: string,
  payload: StartEvalRunPayload,
): Promise<{ id: string; status: string }> => {
  const networkPayload = await getRequestAuthPayload({
    method: 'post',
    url: `/submissions/${submissionId}/eval_runs`,
    data: payload,
  });
  const response = await axios(networkPayload);
  return response.data;
};

export const cancelEvalRun = async (
  submissionId: string,
  runId: string,
  payload: Omit<CancelEvalRunPayload, 'operation'> = {},
): Promise<void> => {
  const networkPayload = await getRequestAuthPayload({
    method: 'patch',
    url: `/submissions/${submissionId}/eval_runs/${runId}`,
    data: {
      operation: ApiPatchOperation.CancelEvalRun,
      ...payload,
    },
  });
  await axios(networkPayload);
};

export const gradeEvalFinding = async (
  submissionId: string,
  runId: string,
  payload: Omit<GradeEvalFindingPayload, 'operation'>,
): Promise<void> => {
  const networkPayload = await getRequestAuthPayload({
    method: 'patch',
    url: `/submissions/${submissionId}/eval_runs/${runId}`,
    data: {
      operation: ApiPatchOperation.GradeEvalFinding,
      ...payload,
    },
  });
  await axios(networkPayload);
};

/**
 * Kicks off the AI resolution check against a completed run. Returns the new
 * check's id + PENDING status; progress then arrives via the run-detail
 * query's `resolution_checks`, which the drawer polls.
 */
export const startResolutionCheck = async (
  submissionId: string,
  runId: string,
): Promise<{ id: string; status: EvalRunStatus }> => {
  const networkPayload = await getRequestAuthPayload({
    method: 'patch',
    url: `/submissions/${submissionId}/eval_runs/${runId}`,
    data: {
      operation: ApiPatchOperation.StartEvalResolutionCheck,
    },
  });
  const response = await axios(networkPayload);
  return response.data;
};

/** Appends one broker comment to a finding's thread. */
export const commentEvalFinding = async (
  submissionId: string,
  runId: string,
  payload: Omit<CommentEvalFindingPayload, 'operation'>,
): Promise<void> => {
  const networkPayload = await getRequestAuthPayload({
    method: 'patch',
    url: `/submissions/${submissionId}/eval_runs/${runId}`,
    data: {
      operation: ApiPatchOperation.CommentEvalFinding,
      ...payload,
    },
  });
  await axios(networkPayload);
};

/** `resolution: null` reopens a resolved / dismissed finding. */
export const resolveEvalFinding = async (
  submissionId: string,
  runId: string,
  payload: Omit<ResolveEvalFindingPayload, 'operation'>,
): Promise<void> => {
  const networkPayload = await getRequestAuthPayload({
    method: 'patch',
    url: `/submissions/${submissionId}/eval_runs/${runId}`,
    data: {
      operation: ApiPatchOperation.ResolveEvalFinding,
      ...payload,
    },
  });
  await axios(networkPayload);
};
